import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  BrieGPTTitle,
  BrieNewButton,
  BrieSidebarHeader,
  BrieSidebarItem,
  BrieSidebarProject,
} from "../../../PageView/PageViewBrieGPT/BrieGPTSidebar";
import {
  BrieGPTChat,
  useBrieGPTChats,
  useBrieGPTProjects,
} from "../../../PageView/PageViewBrieGPT/useBrieGPTItems";
import { BrieGPTTabNavigator } from "../../../PageView/PageViewBrieGPT/BrieGPTSidebarElements/BrieGPTTabNavigator";
import { BrieGPTTabs } from "../../../PageView/PageViewBrieGPT/BrieGPTSidebarElements/Types";
import ProjectSettingsDrawer from "../../../PageView/PageViewBrieGPT/ProjectSettingsDrawer";
import useUpdateBrieGPTUser from "../../../PageView/PageViewBrieGPT/useUpdateBrieGPTUser";
import useDeleteGPTProject from "../../../PageView/PageViewBrieGPT/useDeleteGPTProject";
import ChatSettingsDrawer from "../../../PageView/PageViewBrieGPT/ChatSettingsDrawer";
import useDeleteGPTChat from "../../../PageView/PageViewBrieGPT/useDeleteGPTChat";
import NewProjectModal from "../../../PageView/PageViewBrieGPT/NewProjectModal";
import useGPTUser from "../../../PageView/PageViewBrieGPT/useBrieGPTUser";
import SkeletonLoader from "../../../../elements/SkeletonLoader";
import StrUtils from "../../../../utils/StrUtils";

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  recordID: string | undefined;
  moduleAPIName: string;
  tab: BrieGPTTabs;
  setTab: Dispatch<SetStateAction<BrieGPTTabs>>;
}

export default function BrieChatWithGCSidebar({
  open,
  setOpen,
  recordID,
  moduleAPIName,
  tab,
  setTab,
}: Props) {
  const [editChatID, setEditChatID] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const chatID = searchParams.get("chatID");
  const [editProjectID, setEditProjectID] = useState<string | null>(null);

  const projectID = searchParams.get("projectID");
  const { deleteChat, deletingChat } = useDeleteGPTChat(projectID, tab);
  const { deleteProject, deletingProject } = useDeleteGPTProject(tab);
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const { chats: c, chatsLoading } = useBrieGPTChats({
    type: "record",
    recordID,
    moduleAPIName,
  });
  const { projects, projectsLoading } = useBrieGPTProjects({
    type: "record",
    recordID,
    moduleAPIName,
    tab,
  });

  // GPT User hooks
  const { gptUser } = useGPTUser();
  const { updateGPTUser } = useUpdateBrieGPTUser();

  const pinnedChatsIDs = useMemo(() => {
    return gptUser?.PinnedChats?.map((chat: any) => chat?.Chat?.id) || [];
  }, [gptUser?.PinnedChats]);

  // Stores the sorted chats
  const chats = useMemo(() => {
    if (!c || !Array.isArray(c)) return [];

    // Just return the chats if there are no pinned chats.
    if (!pinnedChatsIDs.length) return c;

    return c.reduce((sortingChats, chat) => {
      // Moving the chat to the top if it's pinned.
      if (pinnedChatsIDs.includes(chat.id)) {
        sortingChats.unshift(chat);
      } else {
        // No pinned chat, just push it to the end.
        sortingChats.push(chat);
      }

      return sortingChats;
    }, [] as BrieGPTChat[]);
  }, [c, pinnedChatsIDs]);

  if (!open) {
    return (
      <div
        className="tw:bg-superlight tw:w-fit tw:px-2 tw:py-1 btn-oauth tw:cursor-pointer tw:mb-1"
        onClick={() => setOpen(true)}
      >
        <i className="mdi mdi-chevron-right tw:text-dark tw:text-2xl" />
      </div>
    );
  }

  return (
    <>
      <div
        className={
          "tw:bg-superlight tw:z-10 tw:absolute tw:md:static tw:w-full tw:h-full"
        }
      >
        <div className="tw:flex tw:justify-end">
          <div className="bg-white tw:flex tw:items-center tw:justify-center tw:px-2 tw:py-1 btn-oauth tw:rounded">
            <i
              className="mdi mdi-chevron-left tw:cursor-pointer tw:text-2xl"
              onClick={() => setOpen(false)}
            />
          </div>
        </div>
        <div className="tw:flex tw:justify-center">
          <div className="tw:m-4 tw:w-full tw:max-w-md tw:pb-8 tw:bg-white tw:rounded tw:sm:min-h-64 tw:overflow-y-auto tw:h-[calc(100vh_-_110px)]">
            <div className="tw:px-2">
              <BrieGPTTitle />
              <BrieGPTTabNavigator tab={tab} setTab={setTab} />
            </div>
            <div className="tw:px-4 ">
              <BrieSidebarHeader
                title="Projects"
                onClick={() => setNewProjectOpen(true)}
                showAdd={projects && projects?.length > 0}
              />
              {!projects || projects?.length === 0 ? (
                projectsLoading ? (
                  <LoadingChats />
                ) : (
                  <BrieNewButton
                    title="New Project"
                    onClick={() => setNewProjectOpen(true)}
                    disabled={false}
                  />
                )
              ) : projectsLoading ? (
                <LoadingChats />
              ) : (
                projects.map((p) => {
                  return (
                    <BrieSidebarProject
                      key={p.id}
                      title={p.Name}
                      id={p.id}
                      chats={p.Chats}
                      deleteChat={({ chatID }) => {
                        if (!recordID) return;
                        deleteChat({
                          type: "record",
                          chatID: chatID,
                          recordID,
                        });
                      }}
                      deletingChat={deletingChat}
                      deleteProject={deleteProject}
                      deletingProject={deletingProject}
                      editProjectID={editProjectID}
                      setEditProjectID={setEditProjectID}
                      editChatID={editChatID}
                      setEditChatID={setEditChatID}
                      onSidebarItemClick={(chatID) =>
                        onSidebarItemClick(p.id, chatID)
                      }
                      type="record"
                      recordID={recordID || ""}
                      moduleAPIName={moduleAPIName}
                      pinnedChatsIDs={pinnedChatsIDs}
                      onPin={(chat) => handlePinChat(chat)}
                    />
                  );
                })
              )}
              {tab !== "Team" && (
                <>
                  <BrieSidebarHeader
                    title="My Chats"
                    onClick={newPrivateChat}
                    showAdd={chats && chats?.length > 0}
                  />
                  {!chats || chats?.length === 0 ? (
                    chatsLoading ? (
                      <LoadingChats />
                    ) : (
                      <BrieNewButton
                        title="New Chat"
                        onClick={newPrivateChat}
                        disabled={false}
                      />
                    )
                  ) : chatsLoading ? (
                    <LoadingChats />
                  ) : (
                    chats.map((chat) => {
                      return (
                        <BrieSidebarItem
                          key={chat.id}
                          title={chat.Name}
                          id={chat.id}
                          onDelete={() => {
                            if (!recordID) return;
                            deleteChat({
                              type: "record",
                              chatID: chat.id,
                              recordID,
                            });
                          }}
                          deletingChat={deletingChat}
                          editChatID={editChatID}
                          setEditChatID={setEditChatID}
                          isSelected={chat.id === chatID}
                          onClick={(id) => onSidebarItemClick(null, id)}
                          pinned={pinnedChatsIDs.includes(chat.id)}
                          onPin={() => handlePinChat(chat)}
                        />
                      );
                    })
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <NewProjectModal
        open={newProjectOpen}
        onClose={() => setNewProjectOpen(false)}
        tab={tab}
        setTab={setTab}
        type="record"
        recordID={recordID || ""}
        moduleAPIName={moduleAPIName}
      />
      <ProjectSettingsDrawer
        open={!!editProjectID}
        onClose={() => setEditProjectID(null)}
        projectID={editProjectID}
        tab={tab}
        type="record"
        recordID={recordID || ""}
        moduleAPIName={moduleAPIName}
      />
      <ChatSettingsDrawer
        open={!!editChatID}
        onClose={() => setEditChatID(null)}
        chatID={editChatID}
        type="record"
        recordID={recordID}
        moduleAPIName={moduleAPIName}
        projectID={projectID}
        tab={tab}
      />
    </>
  );

  function onSidebarItemClick(projectID: string | null, chatID: string | null) {
    if (chatID) {
      searchParams.set("chatID", chatID);
    } else {
      searchParams.delete("chatID");
    }

    if (projectID) {
      searchParams.set("projectID", projectID);
    } else {
      searchParams.delete("projectID");
    }

    setOpen(false);
    setSearchParams(searchParams);
  }

  function newPrivateChat() {
    searchParams.set("chatID", "new");
    setSearchParams(searchParams);
    setOpen(false);
  }

  function handlePinChat(chat: BrieGPTChat) {
    updateGPTUser({
      payload: {
        PinnedChat: {
          id: StrUtils.uuidv4(),
          Chat: {
            id: chat.id,
            _RecordName: chat.Name,
            _ModuleAPIName: "BrieGPTChat",
          },
        },
      },
    });
  }
}

function LoadingChats() {
  return (
    <>
      <SkeletonLoader height={28} />
      <SkeletonLoader height={28} />
    </>
  );
}
